import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { htmlToQuillDelta } from './quillHtmlToDelta.ts';
import { convertQuillHtmlToInlineAndClassHtml } from './convertQuillHtmlToInlineAndClassHtml.js';


export const fontSizes = ['6px', '8px', '10px', '12px', '15px', '18px', '24px'];
export const fontFamilies = ['montserrat'];

// FONT: use class-based attribution
const Font = Quill.import('attributors/class/font');
Font.whitelist = fontFamilies;
Quill.register(Font, true);

// SIZE: use class-based attribution (not style!)
const Size = Quill.import('attributors/class/size');
Size.whitelist = fontSizes;
Quill.register(Size, true);

const Editor = forwardRef(
  (
    {
      readOnly,
      defaultValue,
      onTextChange,
      onSelectionChange,
      onHtmlChange,
      hasError,
      afterComponent,
      defaultColor,
      defaultFontSize,
      bgcolor,
      lang,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

    const containerRef = useRef(null);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onHtmlChangeRef = useRef(onHtmlChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onHtmlChangeRef.current = onHtmlChange;
    });

    useEffect(() => {
      ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: {
            container: '#custom-toolbar', // ID of your MUI toolbar container
          },
        },
      });

      ref.current = quill;

      // Enable browser-native spellcheck and set language
      // Use DOM properties (not just attributes) for reliable browser support
      quill.root.spellcheck = true;
      quill.root.setAttribute('lang', lang || 'de');
      // iOS Safari: enable autocorrect and autocapitalize
      quill.root.setAttribute('autocorrect', 'on');
      quill.root.setAttribute('autocapitalize', 'sentences');

      if (defaultValue) {
        quill.setContents(htmlToQuillDelta(defaultValue));
      }

      // Set default font size as real Quill format (only if prop provided)
      if (defaultFontSize) {
        quill.setSelection(0, 0, 'silent');
        quill.format('size', defaultFontSize);
      }

      // iOS Composition Guard: prevent duplicate text from iOS autocomplete/dictation
      let isComposing = false;
      let pendingHtmlUpdate = false;

      quill.root.addEventListener('compositionstart', () => {
        isComposing = true;
      });

      quill.root.addEventListener('compositionend', () => {
        isComposing = false;
        // Fire deferred HTML change after composition completes
        if (pendingHtmlUpdate) {
          pendingHtmlUpdate = false;
          const quillHtml = quill.root.innerHTML;
          const plainText = quill.getText().trim();
          const inlineHtml = convertQuillHtmlToInlineAndClassHtml(quillHtml);
          onHtmlChangeRef.current?.(inlineHtml, plainText);
        }
      });

      let lastActiveSize = defaultFontSize || null;

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);

        // Preserve font size: use last active size or fall back to default
        const currentFormat = quill.getFormat();
        if (currentFormat.size) {
          lastActiveSize = currentFormat.size;
        } else if (lastActiveSize) {
          quill.format('size', lastActiveSize);
        }

        // Defer HTML processing during composition (iOS autocomplete/dictation)
        if (isComposing) {
          pendingHtmlUpdate = true;
          return;
        }

        const quillHtml = quill.root.innerHTML;
        const plainText = quill.getText().trim();
        const inlineHtml = convertQuillHtmlToInlineAndClassHtml(quillHtml);
        onHtmlChangeRef.current?.(inlineHtml, plainText);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (range, oldRange, source) => {
        // Re-apply font size when cursor moves to a line without size format
        if (range && lastActiveSize) {
          const currentFormat = quill.getFormat(range);
          if (!currentFormat.size) {
            quill.format('size', lastActiveSize);
          } else {
            lastActiveSize = currentFormat.size;
          }
        }
        onSelectionChangeRef.current?.(range, oldRange, source);
      });

      // PASTE HANDLING: Strip font, size, and color so pasted text inherits from CSS
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
        delta.ops.forEach((op) => {
          if (!op.attributes) op.attributes = {};
          delete op.attributes.font;
          delete op.attributes.color;
          // Set defaultFontSize on pasted text, or strip size
          if (defaultFontSize) {
            op.attributes.size = lastActiveSize || defaultFontSize;
          } else {
            delete op.attributes.size;
          }
          op.attributes.code = false;
          op.attributes.background = undefined;
          op.attributes['code-block'] = undefined;
        });
        return delta;
      });

      return () => {
        ref.current = null;
        container.innerHTML = '';
      };
    }, [ref, defaultValue]);

    return (
      <Box
        sx={{
          borderLeft: `4px solid ${hasError ? colors.warning : colors.success}`,
          borderRadius: '12px',
          backgroundColor: bgcolor || colors.bgInput,
          padding: 2,
          '.ql-container': {
            border: '0px solid transparent',
          },
          '.ql-editor': {
            fontSize: defaultFontSize || undefined,
            lineHeight: '1.5',
            padding: 0,
            minHeight: '200px',
            fontFamily: 'montserrat',
            color: 'inherit', // Text color inherits from theme/parent
          },
          '.ql-editor ol, .ql-editor ol li': {
            fontFamily: 'inherit',
            fontSize: 'inherit',
          },
          ol: {
            padding: '0px',
            margin: '0px',
          },
          'li:has(.ql-size-6px)': {
            fontSize: '6px!important',
          },
          'li:has(.ql-size-8px)': {
            fontSize: '8px!important',
          },
          'li:has(.ql-size-10px)': {
            fontSize: '10px!important',
          },
          'li:has(.ql-size-12px)': {
            fontSize: '12px!important',
          },
          'li:has(.ql-size-15px)': {
            fontSize: '15px!important',
          },
          'li:has(.ql-size-18px)': {
            fontSize: '18px!important',
          },
          'li:has(.ql-size-24px)': {
            fontSize: '24px!important',
          },
          '.ql-font-montserrat': {
            fontFamily: 'montserrat',
          },
        }}
        className=" overflow-y-auto"
      >
        <div ref={containerRef}></div> <Box className="px-4" />
        {afterComponent}
      </Box>
    );
  }
);

Editor.displayName = 'Editor';

export default Editor;
